// ── Deep Investigation drawer ────────────────────────────────────────────
//
// Renders the SentinelIR-Agent investigation flow for a single incident:
//   1. On open: fetch the most recent existing run (GET ?customerId).
//      - 404 → no prior run; show the "Start Deep Investigation" CTA.
//      - row in `running` → jump straight to the live progress view +
//        polling loop.
//      - row in `completed` → render the structured Investigation
//        Report. Offer a "Re-run" button so analysts can trigger a fresh
//        investigation as the incident evolves.
//      - row in `failed` → show error + Retry button.
//   2. On Start / Retry: POST → 202 with rowId → flip to live progress
//      view, start polling the row by id every 3s.
//   3. On status='completed' or 'failed' during polling: stop polling,
//      flip to the report view (or error view).
//
// Multi-tenant: every API call includes `customerId`. Write access is gated
// by the `readOnly` prop (derived by the parent from `canWrite('incidents',
// customerId)`, which mirrors the per-customer × per-tab matrix). The server-
// side `requireTabWrite("incidents")` is the source of truth — `readOnly`
// just hides Start/Re-run so users get a visible cue instead of a 403.
//
// Render strategy: we don't pull in ReactMarkdown for the report — we
// render the JSON directly into semantic React components (badges,
// tables, cards) so the analyst gets clickable + accessible content.
// Markdown is only used as a fallback when the structured `report` is
// missing for some reason but `reportMarkdown` exists.

import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { sentinelIncidentNumber } from '../lib/utils';
import remarkGfm from 'remark-gfm';
import { ApiError, apiFetch } from '../lib/apiClient';
import { exportDeepInvestigationReportPdf } from '../lib/deepInvestigationReportPdf';
import { fmtTs } from '../lib/globalTz';
import type { Incident } from '../data/demoData';

// ── Report shape mirrored from server ─────────────────────────────────────

type Verdict = 'TRUE POSITIVE' | 'FALSE POSITIVE' | 'BENIGN POSITIVE' | 'INCONCLUSIVE';
type SeverityAssessment = 'Informational' | 'Low' | 'Medium' | 'High' | 'Critical';

interface EvidenceCitation { source_table: string; detail: string; }
interface TimelineEvent { ts: string; event: string; source_table: string; }
interface KqlQueryRecord { purpose: string; query: string; rows_returned?: number; }
interface MitreMapping { tactic: string; technique_id: string; technique_name: string; evidence: string; }
interface KeyFinding { title: string; detail: string; evidence: EvidenceCitation[]; }
interface Scope {
  users: string[]; hosts: string[]; ips: string[]; files: string[]; processes: string[]; urls: string[];
  blast_radius_summary: string;
}
interface RecommendedActions { immediate: string[]; short_term: string[]; long_term: string[]; }

interface InvestigationReport {
  verdict: Verdict;
  confidence: number;
  severity_assessment: SeverityAssessment;
  executive_summary: string;
  timeline: TimelineEvent[];
  scope: Scope;
  mitre_attack: MitreMapping[];
  key_findings: KeyFinding[];
  recommended_actions: RecommendedActions;
  kql_queries_used: KqlQueryRecord[];
  confidence_explanation: string;
  unanswered_questions: string[];
}

interface DeepInvestigationPayload {
  rowId: string;
  customerId: string;
  incidentId: string;
  incidentNumber: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  triggeredBy: string;
  sessionId: string | null;
  promptVersion: string;
  aiModel: string | null;
  turnsUsed: number;
  kqlQueriesRun: number;
  report: InvestigationReport | null;
  reportMarkdown: string | null;
  error: string | null;
  enqueuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

interface StartResponse {
  rowId: string;
  status: 'running' | 'already_running';
  existingRowId: string | null;
}

// ── Props ─────────────────────────────────────────────────────────────────

interface Props {
  incident: Incident;
  customerId: string;
  /** When true, hides Start / Re-run controls. Parent derives this from
   *  `canWrite('incidents', customerId)` so the matrix is authoritative. */
  readOnly: boolean;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_DURATION_MS = 15 * 60 * 1000; // safety net: 15 minutes

/**
 * Pick the first usable incident identifier for path-segment use.
 *
 * Live Sentinel data (and the current dashboard-data shape) sometimes
 * populates `graphIncidentId` / `armIncidentName` as **empty strings**
 * rather than `null` / `undefined` — typically when the incident hasn't
 * been mirrored into Microsoft Graph yet, or when the ARM resource name
 * isn't tracked by the customer's tenant. Using `??` (nullish coalescing)
 * preserved those empty strings and produced URLs like
 * `/api/incidents//deep-investigate`, which Express collapsed to
 * `/api/incidents/deep-investigate` and bounced as 400/404.
 *
 * The trim() guard also defends against whitespace-only values from
 * misbehaving upstream sources. Returns `null` if nothing usable exists
 * — callers must surface a friendly error rather than firing a doomed
 * request.
 */
function pickIncidentRef(incident: Incident): string | null {
  const candidates = [
    incident.graphIncidentId,
    incident.armIncidentName,
    incident.id,
    // Last-resort: incident number, since the API resolves both
    // numeric incident numbers and graph/arm IDs via the same param.
    incident.incidentNumber,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim();
  }
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────

export function DeepInvestigationDrawer({ incident, customerId, readOnly, onClose }: Props) {
  const [row, setRow] = useState<DeepInvestigationPayload | null>(null);
  // `loadingExisting` is true ONLY during the initial GET-latest request.
  // After we know whether a prior run exists, we never show this spinner
  // again — start/retry uses its own `starting` flag.
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [existingError, setExistingError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const pollStartedAtRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollStartedAtRef.current = null;
  }, []);

  const pollRow = useCallback(
    async (rowId: string) => {
      try {
        const fresh = await apiFetch<DeepInvestigationPayload>(
          `/incidents/deep-investigation/${encodeURIComponent(rowId)}?customerId=${encodeURIComponent(customerId)}`,
        );
        setRow(fresh);
        if (fresh.status === 'running' || fresh.status === 'pending') {
          // Stop polling if we've been at it too long — defence against a
          // server-side hang leaving the UI spinning forever. The analyst
          // can still close + reopen the drawer to fetch the latest state.
          if (
            pollStartedAtRef.current !== null &&
            Date.now() - pollStartedAtRef.current > POLL_MAX_DURATION_MS
          ) {
            stopPolling();
            setStartError(
              'Investigation has been running for over 15 minutes. Close + reopen the drawer to refresh.',
            );
            return;
          }
          pollTimerRef.current = setTimeout(() => void pollRow(rowId), POLL_INTERVAL_MS);
        } else {
          stopPolling();
        }
      } catch (e) {
        // Transient errors — keep polling. A persistent error will be
        // visible once the user closes + reopens the drawer.
        console.warn('[DeepInvestigationDrawer] poll error', e);
        if (pollStartedAtRef.current !== null) {
          pollTimerRef.current = setTimeout(() => void pollRow(rowId), POLL_INTERVAL_MS);
        }
      }
    },
    [customerId, stopPolling],
  );

  const startPolling = useCallback(
    (rowId: string) => {
      stopPolling();
      pollStartedAtRef.current = Date.now();
      pollTimerRef.current = setTimeout(() => void pollRow(rowId), POLL_INTERVAL_MS);
    },
    [pollRow, stopPolling],
  );

  // Initial load: GET latest existing run.
  useEffect(() => {
    let cancelled = false;
    const incidentRef = pickIncidentRef(incident);
    if (!incidentRef) {
      // No usable identifier at all — surface a clear error instead of
      // firing a request that's guaranteed to 400/404. The CTA below
      // will be disabled by the same guard.
      setExistingError(
        'Cannot resolve a Sentinel identifier for this incident. The incident is missing graphIncidentId, armIncidentName, id, and incidentNumber - likely an incomplete or stale dashboard payload.',
      );
      setLoadingExisting(false);
      return () => { cancelled = true; };
    }
    void (async () => {
      try {
        const existing = await apiFetch<DeepInvestigationPayload>(
          `/incidents/${encodeURIComponent(incidentRef)}/deep-investigation?customerId=${encodeURIComponent(customerId)}`,
        );
        if (cancelled) return;
        setRow(existing);
        if (existing.status === 'running' || existing.status === 'pending') {
          startPolling(existing.rowId);
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          // No prior run — that's fine, show the CTA.
          setRow(null);
        } else {
          const msg = e instanceof Error ? e.message : String(e);
          setExistingError(msg);
        }
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id, customerId]);

  const startRun = useCallback(async () => {
    if (readOnly) return; // belt-and-braces; the button is also hidden.
    setStartError(null);
    setStarting(true);
    try {
      const incidentRef = pickIncidentRef(incident);
      if (!incidentRef) {
        // Same defensive bail as the initial-load effect — never let an
        // empty path segment hit the wire.
        throw new Error(
          'Cannot resolve a Sentinel identifier for this incident (no graphIncidentId, armIncidentName, id, or incidentNumber).',
        );
      }
      const resp = await apiFetch<StartResponse>(
        `/incidents/${encodeURIComponent(incidentRef)}/deep-investigate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId }),
        },
      );
      // Optimistically construct a `running` row so the UI flips
      // immediately; the next poll will fill in the real shape.
      setRow({
        rowId: resp.rowId,
        customerId,
        incidentId: incidentRef,
        incidentNumber: String(incident.incidentNumber ?? incident.id),
        status: 'running',
        triggeredBy: 'me',
        sessionId: null,
        promptVersion: 'deep-v1.0',
        aiModel: null,
        turnsUsed: 0,
        kqlQueriesRun: 0,
        report: null,
        reportMarkdown: null,
        error: null,
        enqueuedAt: new Date().toISOString(),
        startedAt: new Date().toISOString(),
        finishedAt: null,
      });
      startPolling(resp.rowId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStartError(msg);
    } finally {
      setStarting(false);
    }
  }, [customerId, incident, readOnly, startPolling]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-stretch justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl h-full bg-card border-l border-border shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-border bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/40 dark:to-blue-950/40">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xl">🔬</span>
              <h2 className="text-lg font-semibold text-foreground">Deep Investigation</h2>
              <span className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-500/30">
                SentinelIR-Agent
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1 truncate">
              Incident #{sentinelIncidentNumber(incident)} - {incident.title}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loadingExisting ? (
            <CenteredSpinner label="Loading…" />
          ) : existingError ? (
            <ErrorBlock title="Could not load existing investigation" detail={existingError} />
          ) : row === null ? (
            <CtaView
              readOnly={readOnly}
              starting={starting}
              startError={startError}
              onStart={startRun}
            />
          ) : row.status === 'pending' || row.status === 'running' ? (
            <ProgressView row={row} />
          ) : row.status === 'failed' ? (
            <FailedView
              row={row}
              readOnly={readOnly}
              starting={starting}
              startError={startError}
              onRetry={startRun}
            />
          ) : row.status === 'cancelled' ? (
            <ErrorBlock title="Investigation cancelled" detail={row.error ?? 'No detail.'} />
          ) : (
            <ReportView
              row={row}
              incident={incident}
              customerId={customerId}
              readOnly={readOnly}
              starting={starting}
              startError={startError}
              onReRun={startRun}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function CenteredSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
      <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
      <div className="text-sm">{label}</div>
    </div>
  );
}

function ErrorBlock({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="p-6">
      <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-4">
        <div className="font-semibold text-red-700 dark:text-red-300 mb-1">{title}</div>
        <div className="text-sm text-red-700/80 dark:text-red-300/80 whitespace-pre-wrap font-mono">{detail}</div>
      </div>
    </div>
  );
}

function CtaView({
  readOnly,
  starting,
  startError,
  onStart,
}: {
  readOnly: boolean;
  starting: boolean;
  startError: string | null;
  onStart: () => void;
}) {
  return (
    <div className="p-6 space-y-4">
      <div className="rounded-lg border border-border bg-muted/30 p-5">
        <h3 className="font-semibold text-foreground mb-2">What this does</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Runs the SentinelIR-Agent - a Tier-2/Tier-3 autonomous investigator that pivots across
          every entity in this incident, builds a chronological timeline, maps findings to MITRE
          ATT&amp;CK, classifies the verdict, and drafts containment recommendations. Read-only:
          executes only KQL hunts against your Log Analytics workspace, never destructive
          actions. Typically takes 1–3 minutes.
        </p>
      </div>

      {readOnly ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-700 dark:text-amber-300">
          Deep investigation is restricted to SOC analysts. Contact your administrator to escalate.
        </div>
      ) : (
        <>
          <button
            onClick={onStart}
            disabled={starting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold transition-colors"
          >
            {starting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Starting…
              </>
            ) : (
              <>🔬 Start Deep Investigation</>
            )}
          </button>
          {startError && (
            <div className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap font-mono">{startError}</div>
          )}
        </>
      )}
    </div>
  );
}

// Mirrors `MAX_AGENT_TURNS` in artifacts/api-server/src/lib/agentLoop.ts.
// The deep-investigation agent loop hard-caps at 10 turns; we use that as the
// denominator for the progress bar. Keep in sync if the server budget moves.
const DEEP_INVESTIGATION_MAX_TURNS = 10;

function ProgressView({ row }: { row: DeepInvestigationPayload }) {
  // While the agent is still running we cap the displayed percentage at 95%
  // so the bar never claims "done" before the synthesis pass actually
  // completes. The bar jumps to 100% the moment status flips to 'completed'
  // — at which point this view is replaced by ReportView anyway.
  const isRunning = row.status === 'running' || row.status === 'pending';
  const rawPct = Math.round((row.turnsUsed / DEEP_INVESTIGATION_MAX_TURNS) * 100);
  const pct = Math.max(2, Math.min(isRunning ? 95 : 100, rawPct));
  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-6 h-6 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground">Investigating…</div>
          <div className="text-xs text-muted-foreground">
            Turn {row.turnsUsed} of up to {DEEP_INVESTIGATION_MAX_TURNS} · {row.kqlQueriesRun} KQL {row.kqlQueriesRun === 1 ? 'query' : 'queries'} run
          </div>
        </div>
        <div className="text-sm font-semibold text-purple-700 dark:text-purple-300 tabular-nums" aria-label="Investigation progress">
          {pct}%
        </div>
      </div>
      {/* Determinate progress bar driven by turnsUsed/MAX_TURNS. The agent
          can finish early (synthesis on turn 5 of 10), so a "real" complete
          state is the status flipping to 'completed' — not the bar reaching
          100%. We cap at 95% while running to communicate that. */}
      <div
        className="h-1.5 w-full rounded-full bg-muted overflow-hidden mb-4"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-purple-500 transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="rounded-lg border border-border bg-muted/30 p-4 text-xs text-muted-foreground leading-relaxed">
        The agent is mining entities, pivoting across SecurityAlert, SigninLogs, Device*Events, OfficeActivity, and
        related tables, then synthesising a structured Investigation Report. The drawer will refresh automatically.
        You can close this panel - the investigation continues in the background and the result will be available
        next time you open it.
      </div>
      {row.sessionId && (
        <div className="mt-3 text-[10px] font-mono text-muted-foreground/60">
          Session: {row.sessionId}
        </div>
      )}
    </div>
  );
}

function FailedView({
  row,
  readOnly,
  starting,
  startError,
  onRetry,
}: {
  row: DeepInvestigationPayload;
  readOnly: boolean;
  starting: boolean;
  startError: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="p-6 space-y-4">
      <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-4">
        <div className="font-semibold text-red-700 dark:text-red-300 mb-1">Investigation failed</div>
        <div className="text-sm text-red-700/80 dark:text-red-300/80 whitespace-pre-wrap font-mono break-words">
          {row.error ?? 'No detail provided.'}
        </div>
      </div>
      {!readOnly && (
        <>
          <button
            onClick={onRetry}
            disabled={starting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold transition-colors"
          >
            {starting ? 'Restarting…' : 'Retry investigation'}
          </button>
          {startError && (
            <div className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap font-mono">{startError}</div>
          )}
        </>
      )}
    </div>
  );
}

function ReportView({
  row,
  incident,
  customerId,
  readOnly,
  starting,
  startError,
  onReRun,
}: {
  row: DeepInvestigationPayload;
  incident: Incident;
  customerId: string;
  readOnly: boolean;
  starting: boolean;
  startError: string | null;
  onReRun: () => void;
}) {
  const report = row.report;
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function downloadPdf() {
    if (!report) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const result = await exportDeepInvestigationReportPdf(
        {
          incidentId: row.incidentId,
          incidentNumber: row.incidentNumber,
          incidentTitle: incident.title,
          customerId,
          customerName: customerId,
          generatedAt: row.finishedAt ?? new Date().toISOString(),
          aiModel: row.aiModel,
          promptVersion: row.promptVersion,
          turnsUsed: row.turnsUsed,
          kqlQueriesRun: row.kqlQueriesRun,
          report,
        },
        { customerName: customerId },
      );
      if (!result.ok) setPdfError(result.error ?? 'PDF generation failed');
    } finally {
      setPdfLoading(false);
    }
  }
  if (!report) {
    // Shouldn't normally happen — completed runs should always have a
    // structured report. Fall back to the markdown if the server somehow
    // persisted only that.
    return (
      <div className="p-6 space-y-4">
        {row.reportMarkdown ? (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{row.reportMarkdown}</ReactMarkdown>
          </div>
        ) : (
          <ErrorBlock title="Investigation completed but no report" detail="The pipeline reported success but did not persist a structured report." />
        )}
        {!readOnly && <ReRunButton starting={starting} startError={startError} onReRun={onReRun} />}
      </div>
    );
  }

  return (
    <div className="p-5 space-y-5">
      {/* Top-line verdict */}
      <div className="flex flex-wrap items-center gap-2">
        <VerdictBadge verdict={report.verdict} confidence={report.confidence} />
        <SeverityBadge severity={report.severity_assessment} />
        <span className="text-[10px] text-muted-foreground font-mono">
          {row.kqlQueriesRun} KQL queries
        </span>
        <button
          type="button"
          onClick={() => void downloadPdf()}
          disabled={pdfLoading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-semibold text-foreground disabled:opacity-50 transition-colors"
          title="Download this investigation report as a branded PDF"
        >
          {pdfLoading ? 'Generating…' : 'Download PDF'}
        </button>
      </div>
      {pdfError && <p className="text-xs text-red-600 dark:text-red-400">{pdfError}</p>}

      {/* Executive summary */}
      <Section title="Executive summary">
        <p className="text-sm text-foreground leading-relaxed">{report.executive_summary}</p>
      </Section>

      {/* Timeline */}
      {report.timeline.length > 0 && (
        <Section title={`Timeline (${report.timeline.length})`}>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">When</th>
                  <th className="text-left px-3 py-2 font-semibold">Event</th>
                  <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Source</th>
                </tr>
              </thead>
              <tbody>
                {report.timeline.map((t, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-mono whitespace-nowrap text-muted-foreground">{t.ts ? fmtTs(t.ts) : t.ts}</td>
                    <td className="px-3 py-2 text-foreground">{t.event}</td>
                    <td className="px-3 py-2 font-mono text-[10px] whitespace-nowrap text-muted-foreground">{t.source_table}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Scope */}
      <Section title="Scope & blast radius">
        <p className="text-sm text-foreground leading-relaxed mb-3">{report.scope.blast_radius_summary}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <ScopeChips label="Users" items={report.scope.users} colour="blue" />
          <ScopeChips label="Hosts" items={report.scope.hosts} colour="amber" />
          <ScopeChips label="IPs" items={report.scope.ips} colour="rose" />
          <ScopeChips label="Files" items={report.scope.files} colour="slate" />
          <ScopeChips label="Processes" items={report.scope.processes} colour="emerald" />
          <ScopeChips label="URLs" items={report.scope.urls} colour="violet" />
        </div>
      </Section>

      {/* MITRE */}
      {report.mitre_attack.length > 0 && (
        <Section title="MITRE ATT&CK">
          <ul className="space-y-2">
            {report.mitre_attack.map((m, i) => (
              <li key={i} className="text-xs">
                <span className="inline-block px-2 py-0.5 mr-2 rounded bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30 font-mono font-semibold">
                  {m.technique_id}
                </span>
                <span className="font-semibold text-foreground">{m.technique_name}</span>
                <span className="text-muted-foreground"> · {m.tactic}</span>
                <div className="mt-0.5 ml-1 text-muted-foreground italic">{m.evidence}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Key findings */}
      {report.key_findings.length > 0 && (
        <Section title={`Key findings (${report.key_findings.length})`}>
          <div className="space-y-3">
            {report.key_findings.map((f, i) => (
              <div key={i} className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="font-semibold text-sm text-foreground mb-1">{f.title}</div>
                <div className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{f.detail}</div>
                {f.evidence.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">Evidence</div>
                    <ul className="space-y-1">
                      {f.evidence.map((e, j) => (
                        <li key={j} className="text-xs text-muted-foreground">
                          <span className="font-mono text-foreground/80">{e.source_table}</span> - {e.detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Recommended actions */}
      <Section title="Recommended actions">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ActionColumn label="Immediate" tone="red" items={report.recommended_actions.immediate} />
          <ActionColumn label="Short-term" tone="amber" items={report.recommended_actions.short_term} />
          <ActionColumn label="Long-term" tone="green" items={report.recommended_actions.long_term} />
        </div>
      </Section>

      {/* KQL queries (collapsible) */}
      {report.kql_queries_used.length > 0 && (
        <Section title={`KQL queries executed (${report.kql_queries_used.length})`}>
          <details className="rounded-lg border border-border bg-muted/30">
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted/50">
              Show queries
            </summary>
            <div className="p-3 space-y-3">
              {report.kql_queries_used.map((q, i) => (
                <div key={i}>
                  <div className="text-xs font-semibold text-foreground mb-1">
                    {q.purpose}
                    {q.rows_returned !== undefined && (
                      <span className="ml-2 text-[10px] font-mono text-muted-foreground">({q.rows_returned} rows)</span>
                    )}
                  </div>
                  <pre className="text-[11px] font-mono bg-background/50 border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">{q.query}</pre>
                </div>
              ))}
            </div>
          </details>
        </Section>
      )}

      {/* Confidence */}
      <Section title="Confidence">
        <p className="text-sm text-foreground leading-relaxed">{report.confidence_explanation}</p>
      </Section>

      {/* Re-run */}
      {!readOnly && (
        <div className="pt-3 border-t border-border">
          <ReRunButton starting={starting} startError={startError} onReRun={onReRun} />
        </div>
      )}
    </div>
  );
}

function ReRunButton({
  starting,
  startError,
  onReRun,
}: {
  starting: boolean;
  startError: string | null;
  onReRun: () => void;
}) {
  return (
    <div className="space-y-2">
      <button
        onClick={onReRun}
        disabled={starting}
        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/20 disabled:opacity-50 text-purple-700 dark:text-purple-300 text-sm font-semibold transition-colors"
      >
        {starting ? 'Re-running…' : '🔬 Re-run investigation'}
      </button>
      {startError && (
        <div className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap font-mono">{startError}</div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">{title}</h3>
      {children}
    </section>
  );
}

function VerdictBadge({ verdict, confidence }: { verdict: Verdict; confidence: number }) {
  const cls =
    verdict === 'TRUE POSITIVE'
      ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40'
      : verdict === 'FALSE POSITIVE' || verdict === 'BENIGN POSITIVE'
        ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/40'
        : 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold border ${cls}`}>
      {verdict}
      <span className="font-mono opacity-80">· {confidence}%</span>
    </span>
  );
}

function SeverityBadge({ severity }: { severity: SeverityAssessment }) {
  const cls =
    severity === 'Critical' ? 'bg-red-700/20 text-red-700 dark:text-red-300 border-red-700/40'
    : severity === 'High'    ? 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40'
    : severity === 'Medium'  ? 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/40'
    : severity === 'Low'     ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40'
    :                          'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border ${cls}`}>
      Effective severity: {severity}
    </span>
  );
}

const SCOPE_COLOURS: Record<string, string> = {
  blue: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
  amber: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  rose: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
  slate: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30',
  emerald: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  violet: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
};

function ScopeChips({ label, items, colour }: { label: string; items: string[]; colour: keyof typeof SCOPE_COLOURS }) {
  if (items.length === 0) return null;
  const chipCls = SCOPE_COLOURS[colour] ?? SCOPE_COLOURS['slate']!;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">
        {label} ({items.length})
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((s, i) => (
          <span key={i} className={`px-1.5 py-0.5 rounded font-mono text-[11px] border ${chipCls}`}>
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActionColumn({ label, tone, items }: { label: string; tone: 'red' | 'amber' | 'green'; items: string[] }) {
  const cls =
    tone === 'red' ? 'border-red-500/40 bg-red-500/5'
    : tone === 'amber' ? 'border-amber-500/40 bg-amber-500/5'
    : 'border-green-500/40 bg-green-500/5';
  return (
    <div className={`rounded-lg border ${cls} p-3`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-foreground mb-2">{label}</div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">None</div>
      ) : (
        <ul className="space-y-1.5 text-xs text-foreground/90">
          {items.map((a, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>{a}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
